import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  CalendarDays,
  ExternalLink,
  FileText,
  Pill,
  RefreshCw,
  ShieldCheck,
  UserRound,
  Video,
} from "lucide-react";

import { Badge, Button, Card } from "@/design-system/primitives";
import {
  useClinicianSession,
  useNurseAppointments,
  useWorklistPatientBrief,
} from "@/lib/api/clinicianHooks";
import type {
  AppointmentWorkItem,
  PatientBriefResponse,
} from "@/types/clinician";
import { PatientChartDrawer, type ChartView } from "./ConversationScreen";
import { intakeSubmissionDate } from "./IntakeHighlights";

type CalendarView = "today" | "upcoming";

export function NurseAppointmentsScreen() {
  const session = useClinicianSession();
  const nurse = session.data?.provider_type === "nurse";
  const calendar = useNurseAppointments(nurse);
  const timezone = calendar.data?.timezone || session.data?.timezone;
  const appointments = calendar.data?.appointments ?? [];
  const [view, setView] = useState<CalendarView>("today");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [chartOpen, setChartOpen] = useState(false);
  const [chartView, setChartView] = useState<ChartView>(
    "overview",
  );

  const grouped = useMemo(
    () => groupAppointments(appointments, timezone),
    [appointments, timezone],
  );
  const visible = view === "today" ? grouped.today : grouped.upcoming;
  const selected =
    appointments.find((appointment) => appointment.id === selectedId) ??
    visible[0] ??
    grouped.today[0] ??
    grouped.upcoming[0] ??
    null;
  const brief = useWorklistPatientBrief(selected?.patient.id ?? null);

  useEffect(() => {
    if (selectedId && appointments.some((item) => item.id === selectedId)) {
      return;
    }
    setSelectedId(grouped.today[0]?.id ?? grouped.upcoming[0]?.id ?? null);
  }, [appointments, grouped.today, grouped.upcoming, selectedId]);

  if (session.isLoading) {
    return <PageMessage>Loading nurse calendar…</PageMessage>;
  }
  if (!nurse) {
    return <Navigate to="/clinician-portal/today" replace />;
  }

  return (
    <div className="max-w-[100rem] mx-auto px-20 md:px-35 py-25 md:py-35 flex flex-col gap-20">
      <header className="flex flex-wrap items-end justify-between gap-15">
        <div>
          <span className="type-label-sm text-ink-muted">NURSING</span>
          <h1 className="type-heading-xl mt-4">Nurse appointments</h1>
          <p className="type-body-sm text-ink-muted mt-5 max-w-[44rem]">
            Guided first self-injection visits assigned to you, with the patient
            chart beside the calendar.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={calendar.isFetching}
          onClick={() => void calendar.refetch()}
        >
          <RefreshCw size={13} aria-hidden /> Refresh
        </Button>
      </header>

      <div className="grid grid-cols-2 border border-line-subtle bg-raised">
        <SummaryCount label="Today" value={grouped.today.length} />
        <SummaryCount label="Upcoming" value={grouped.upcoming.length} />
      </div>

      <div className="flex border-b border-line-subtle" role="tablist">
        <CalendarTab
          active={view === "today"}
          count={grouped.today.length}
          onClick={() => {
            setView("today");
            setSelectedId(grouped.today[0]?.id ?? null);
          }}
        >
          Today
        </CalendarTab>
        <CalendarTab
          active={view === "upcoming"}
          count={grouped.upcoming.length}
          onClick={() => {
            setView("upcoming");
            setSelectedId(grouped.upcoming[0]?.id ?? null);
          }}
        >
          Upcoming
        </CalendarTab>
      </div>

      {calendar.isLoading ? (
        <Card className="p-30 text-center type-body-sm text-ink-muted">
          Loading appointments…
        </Card>
      ) : calendar.isError ? (
        <Card className="p-30 text-center" role="alert">
          <p className="type-body-md font-medium">
            Nurse appointments could not be loaded.
          </p>
          <p className="type-body-sm text-ink-muted mt-4">
            Refresh to try the Healthie calendar again.
          </p>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-[minmax(0,1.55fr)_minmax(19rem,0.8fr)] gap-15 items-start">
          <AppointmentList
            appointments={visible}
            selectedId={selected?.id ?? null}
            timezone={timezone}
            emptyLabel={
              view === "today"
                ? "No guided visits are scheduled today."
                : "No upcoming guided visits are scheduled."
            }
            onSelect={setSelectedId}
          />
          <PatientContext
            appointment={selected}
            data={brief.data}
            loading={brief.isLoading}
            error={brief.isError}
            onOpenChart={() => {
              setChartView("overview");
              setChartOpen(true);
            }}
          />
        </div>
      )}

      {chartOpen && brief.data && (
        <PatientChartDrawer
          open={chartOpen}
          onClose={() => setChartOpen(false)}
          data={brief.data}
          view={chartView}
          onViewChange={setChartView}
        />
      )}
    </div>
  );
}

function AppointmentList({
  appointments,
  selectedId,
  timezone,
  emptyLabel,
  onSelect,
}: {
  appointments: AppointmentWorkItem[];
  selectedId: string | null;
  timezone?: string;
  emptyLabel: string;
  onSelect: (id: string) => void;
}) {
  if (!appointments.length) {
    return (
      <Card className="p-35 min-h-[14rem] flex items-center justify-center text-center">
        <div>
          <CalendarDays
            size={22}
            className="mx-auto text-ink-muted"
            aria-hidden
          />
          <p className="type-body-md font-medium mt-10">Calendar clear</p>
          <p className="type-body-sm text-ink-muted mt-3">{emptyLabel}</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="border border-line-subtle bg-raised divide-y divide-line-subtle">
      {appointments.map((appointment) => {
        const selected = appointment.id === selectedId;
        return (
          <div
            key={appointment.id}
            className={`grid md:grid-cols-[8.5rem_minmax(0,1fr)_auto] gap-10 items-center p-14 border-l-2 ${selected ? "border-l-ink bg-sunken" : "border-l-transparent"}`}
          >
            <button
              type="button"
              onClick={() => onSelect(appointment.id)}
              className="md:col-span-2 grid md:grid-cols-[8.5rem_minmax(0,1fr)] gap-10 min-w-0 text-left focus-visible:outline-none focus-visible:shadow-focus"
              aria-label={`Show chart for ${patientName(appointment)}`}
            >
              <div>
                <p className="type-label-xs text-ink-muted">
                  {formatDate(appointment.starts_at, timezone)}
                </p>
                <p className="type-heading-sm mt-2">
                  {formatTime(appointment.starts_at, timezone)}
                </p>
                {appointment.ends_at && (
                  <p className="text-11 text-ink-muted mt-2">
                    to {formatTime(appointment.ends_at, timezone)}
                  </p>
                )}
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-7">
                  <p className="type-body-md font-medium truncate">
                    {patientName(appointment)}
                  </p>
                  <AppointmentStatus status={appointment.status} />
                </div>
                <p className="type-body-sm text-ink-muted truncate mt-2">
                  {appointment.patient.email}
                </p>
                <p className="type-label-xs text-ink-muted mt-6">
                  {appointment.appointment_type || "Guided self-injection"}
                  {patientLocalTime(appointment, timezone)}
                </p>
              </div>
            </button>
            <div className="flex md:justify-end">
              {appointment.join_url ? (
                <Button asChild size="sm">
                  <a
                    href={appointment.join_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Join visit with ${patientName(appointment)}`}
                  >
                    <Video size={13} aria-hidden /> Join
                  </a>
                </Button>
              ) : (
                <Badge size="sm">Link pending</Badge>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PatientContext({
  appointment,
  data,
  loading,
  error,
  onOpenChart,
}: {
  appointment: AppointmentWorkItem | null;
  data?: PatientBriefResponse;
  loading: boolean;
  error: boolean;
  onOpenChart: () => void;
}) {
  if (!appointment) {
    return (
      <Card className="lg:sticky lg:top-20 p-25 text-center type-body-sm text-ink-muted">
        Select an appointment to review its patient chart.
      </Card>
    );
  }
  const profile = data?.profile;
  const medications = data?.medications ?? [];
  const highlights = [...(data?.intake_evidence ?? [])]
    .sort(
      (left, right) =>
        importanceRank(left.importance) - importanceRank(right.importance),
    )
    .slice(0, 4);

  return (
    <Card padding="none" className="lg:sticky lg:top-20 overflow-hidden">
      <div className="p-15 border-b border-line-subtle flex items-start justify-between gap-10">
        <div className="min-w-0">
          <span className="type-label-xs text-ink-muted">PATIENT CHART</span>
          <h2 className="type-heading-md mt-3 truncate">
            {patientName(appointment)}
          </h2>
          <p className="type-body-sm text-ink-muted truncate mt-2">
            {appointment.patient.email}
          </p>
        </div>
        <UserRound size={20} className="text-ink-muted shrink-0" aria-hidden />
      </div>

      {loading ? (
        <div className="p-20 type-body-sm text-ink-muted">Loading chart…</div>
      ) : error || !data ? (
        <div className="p-20 type-body-sm text-danger">
          Patient chart could not be loaded.
        </div>
      ) : (
        <div className="divide-y divide-line-subtle">
          <section className="p-15">
            <p className="type-label-xs text-ink-muted mb-7">PATIENT BRIEF</p>
            <p className="type-body-sm">
              {[
                profile?.age ? `${profile.age} years` : null,
                profile?.sex,
                profile?.state,
                profile?.timezone,
              ]
                .filter(Boolean)
                .join(" · ") || "Demographics not available"}
            </p>
          </section>

          <section className="p-15">
            <div className="flex items-center gap-6 mb-8">
              <Pill size={13} aria-hidden />
              <p className="type-label-xs">ACTIVE MEDICATIONS</p>
            </div>
            {medications.length ? (
              <ul className="flex flex-col gap-7">
                {medications.slice(0, 3).map((medication) => (
                  <li key={medication.id} className="type-body-sm">
                    <span className="font-medium">{medication.name}</span>
                    {(medication.dose || medication.frequency) && (
                      <span className="block text-11 text-ink-muted mt-1">
                        {[medication.dose, medication.frequency]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="type-body-sm text-ink-muted">None on file.</p>
            )}
          </section>

          <section className="p-15">
            <div className="flex items-center gap-6 mb-8">
              <ShieldCheck size={13} aria-hidden />
              <p className="type-label-xs">CLINICAL HIGHLIGHTS</p>
            </div>
            {highlights.length ? (
              <dl className="flex flex-col gap-8">
                {highlights.map((item, index) => (
                  <div key={`${item.question}-${index}`}>
                    <dt className="text-11 text-ink-muted">{item.question}</dt>
                    <dd className="text-11 text-ink-muted">
                      {item.form_name} ·{" "}
                      {intakeSubmissionDate(item.submitted_at)}
                    </dd>
                    <dd className="type-body-sm mt-1 flex items-start justify-between gap-7">
                      <span>{item.answer}</span>
                      {item.importance === "safety" && (
                        <Badge tone="danger" size="sm">
                          Safety
                        </Badge>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="type-body-sm text-ink-muted">
                No highlighted intake answers.
              </p>
            )}
          </section>

          <section className="p-15 flex items-center justify-between gap-10">
            <span className="flex items-center gap-6 type-body-sm">
              <FileText size={13} aria-hidden />
              {data.documents.length} chart{" "}
              {data.documents.length === 1 ? "file" : "files"}
            </span>
            <span className="type-body-sm text-ink-muted">
              {data.intake_forms.length}{" "}
              {data.intake_forms.length === 1 ? "intake" : "intakes"}
            </span>
          </section>
        </div>
      )}

      <div className="p-12 bg-sunken grid gap-7">
        <Button type="button" size="sm" onClick={onOpenChart} disabled={!data}>
          Quick view chart
        </Button>
        <Button asChild variant="secondary" size="sm">
          <Link to={`/clinician-portal/patients/${appointment.patient.id}`}>
            Open full patient workspace <ExternalLink size={12} aria-hidden />
          </Link>
        </Button>
      </div>
    </Card>
  );
}

function SummaryCount({ label, value }: { label: string; value: number }) {
  return (
    <div className="p-12 border-r last:border-r-0 border-line-subtle">
      <p className="type-label-xs text-ink-muted">{label}</p>
      <p className="type-heading-lg mt-2 tabular-nums">{value}</p>
    </div>
  );
}

function CalendarTab({
  active,
  count,
  onClick,
  children,
}: {
  active: boolean;
  count: number;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`px-15 py-10 type-label-sm border-b-2 -mb-px focus-visible:outline-none focus-visible:shadow-focus ${active ? "border-ink text-ink" : "border-transparent text-ink-muted hover:text-ink"}`}
    >
      {children} <span className="ml-4 tabular-nums">{count}</span>
    </button>
  );
}

function AppointmentStatus({ status }: { status: string | null }) {
  const normalized = (status || "").toLowerCase();
  const tone = normalized.includes("no show")
    ? "danger"
    : normalized.includes("occurred") || normalized.includes("complete")
      ? "success"
      : "neutral";
  return (
    <Badge tone={tone} size="sm">
      {status || "Scheduled"}
    </Badge>
  );
}

function PageMessage({ children }: { children: ReactNode }) {
  return (
    <div className="max-w-[90rem] mx-auto px-20 md:px-40 py-40 type-body-sm text-ink-muted">
      {children}
    </div>
  );
}

function patientName(appointment: AppointmentWorkItem) {
  return (
    appointment.patient.display_name ||
    appointment.patient.email ||
    "Unknown patient"
  );
}

function groupAppointments(
  appointments: AppointmentWorkItem[],
  timezone?: string,
) {
  const todayKey = dateKey(new Date(), timezone);
  return {
    today: appointments.filter(
      (appointment) =>
        dateKey(new Date(appointment.starts_at), timezone) === todayKey,
    ),
    upcoming: appointments.filter(
      (appointment) =>
        dateKey(new Date(appointment.starts_at), timezone) > todayKey,
    ),
  };
}

function dateKey(value: Date, timezone?: string) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      ...(timezone ? { timeZone: timezone } : {}),
    }).formatToParts(value);
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value || "";
    return `${get("year")}-${get("month")}-${get("day")}`;
  } catch {
    return value.toISOString().slice(0, 10);
  }
}

function formatDate(value: string, timezone?: string) {
  return new Date(value).toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(timezone ? { timeZone: timezone } : {}),
  });
}

function formatTime(value: string, timezone?: string) {
  return new Date(value).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    ...(timezone ? { timeZone: timezone } : {}),
  });
}

function patientLocalTime(
  appointment: AppointmentWorkItem,
  clinicianTimezone?: string,
) {
  if (!appointment.timezone || appointment.timezone === clinicianTimezone) {
    return "";
  }
  try {
    return ` · Patient ${new Date(appointment.starts_at).toLocaleTimeString(
      [],
      {
        hour: "numeric",
        minute: "2-digit",
        timeZone: appointment.timezone,
        timeZoneName: "short",
      },
    )}`;
  } catch {
    return "";
  }
}

function importanceRank(value: "safety" | "clinical" | "context") {
  if (value === "safety") return 0;
  if (value === "clinical") return 1;
  return 2;
}
